import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { DocCategory } from "../../config/docs";

interface DocsCardProps {
  category: DocCategory;
}

const icons: Record<string, React.ReactNode> = {
  rocket: (
    <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={1.5}
        d="M15.59 14.37a6 6 0 01-5.84 7.38v-4.8m5.84-2.58a14.98 14.98 0 006.16-12.12A14.98 14.98 0 009.631 8.41m5.96 5.96a14.926 14.926 0 01-5.841 2.58m-.119-8.54a6 6 0 00-7.381 5.84h4.8m2.581-5.84a14.927 14.927 0 00-2.58 5.84m2.699 2.7c-.103.021-.207.041-.311.06a15.09 15.09 0 01-2.448-2.448 14.9 14.9 0 01.06-.312m-2.24 2.39a4.493 4.493 0 00-1.757 4.306 4.493 4.493 0 004.306-1.758M16.5 9a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0z"
      />
    </svg>
  ),
  cube: (
    <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={1.5}
        d="M21 7.5l-9-5.25L3 7.5m18 0l-9 5.25m9-5.25v9l-9 5.25M3 7.5l9 5.25M3 7.5v9l9 5.25m0-9v9"
      />
    </svg>
  ),
  book: (
    <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={1.5}
        d="M12 6.042A8.967 8.967 0 006 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 016 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 016-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0018 18a8.967 8.967 0 00-6 2.292m0-14.25v14.25"
      />
    </svg>
  ),
  code: (
    <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={1.5}
        d="M17.25 6.75L22.5 12l-5.25 5.25m-10.5 0L1.5 12l5.25-5.25m7.5-3l-4.5 16.5"
      />
    </svg>
  ),
};

export function DocsCard({ category }: DocsCardProps) {
  const { t } = useTranslation();
  const firstDoc = category.items[0];

  return (
    <Link
      to={`/docs/${category.id}/${firstDoc.slug}`}
      className="group block p-6 bg-dark-card border border-dark-border rounded-xl hover:border-warm-600 transition-colors"
    >
      <div className="flex items-start gap-4">
        <div className="text-warm-500 group-hover:text-warm-400 transition-colors">
          {icons[category.icon]}
        </div>
        <div className="flex-1">
          <h3 className="text-lg font-semibold text-warm-50 group-hover:text-warm-100 transition-colors">
            {t(category.titleKey)}
          </h3>
          <p className="text-sm text-warm-500 mt-1">{t(category.descriptionKey)}</p>
          <ul className="mt-4 space-y-1.5">
            {category.items.slice(0, 4).map((item) => (
              <li key={item.slug} className="text-sm text-warm-400 flex items-center gap-2">
                <svg className="w-3 h-3 text-warm-600" fill="currentColor" viewBox="0 0 20 20">
                  <path
                    fillRule="evenodd"
                    d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                    clipRule="evenodd"
                  />
                </svg>
                {t(item.titleKey)}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </Link>
  );
}
